import "@/index.css";
import "@/App.css";
import { Suspense } from "react";
import { BrowserRouter, Routes, Route, Outlet, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";

import Header from "./components/Header";
import TexasBanner from "./components/TexasBanner";
import TickerTape from "./components/TickerTape";
import Footer from "./components/Footer";
import ErrorBoundary from "./components/ErrorBoundary";
import NotFound from "./components/NotFound";
import Home from "./pages/Home";
import StateLanding from "./pages/marketing/StateLanding";
import CommercialIntake from "./pages/commercial/CommercialIntake";
import Plans from "./pages/Plans";
import PlanDetail from "./pages/PlanDetail";
import Enroll from "./pages/enroll/Enroll";
import Providers from "./pages/Providers";
import HowItWorks from "./pages/HowItWorks";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import EsidLookup from "./pages/EsidLookup";
import AdminLogin from "./pages/AdminLogin";
import SignIn from "./pages/account/SignIn";
import Register from "./pages/account/Register";
import VerifyEmail from "./pages/account/VerifyEmail";
import AccountOverview from "./pages/account/AccountOverview";
import { StateGate, Guard } from "./routes/StateRoutes";
import Seo from "./components/Seo";
import JsonLd from "./components/JsonLd";
import { buildSiteJsonLd } from "./lib/jsonLd";
import { lazyRoute } from "./lib/lazyRoute";

// Heavy or rarely-visited routes are code-split (bundle budget). recharts only loads with MarketPulse.
const MarketPulse = lazyRoute(() => import("./pages/MarketPulse"));
const DataCenters = lazyRoute(() => import("./pages/DataCenters"));
const Utilities = lazyRoute(() => import("./pages/Utilities"));
const Solar = lazyRoute(() => import("./pages/Solar"));
const AdminDashboard = lazyRoute(() => import("./pages/AdminDashboard"));
// Launch-optional (Product Contract §7); code-split so its Turnstile/upload code never loads for visitors
// who don't use it.
const BillCheck = lazyRoute(() => import("./pages/BillCheck"));
// ETF Education And Buyout Promo Placement Spec.md (Reba-CPO, 2026-09-29), Section 3: single source-of-truth
// page for ETF-exception education. Rarely a direct entry point (mostly reached via a link), so code-split.
const EarlyTerminationFees = lazyRoute(() => import("./pages/help/EarlyTerminationFees"));
// Answer pages (marketing/answer_pages, SEO spec Section 4): /help hub and /help/<slug>, code-split.
const Help = lazyRoute(() => import("./pages/help/Help"));
const AnswerPage = lazyRoute(() => import("./pages/help/AnswerPage"));
// About (CLO-approved copy), same generated content source and building blocks.
const About = lazyRoute(() => import("./pages/About"));
// Newsletter signup (flag-gated) and its token pages (confirm, unsubscribe, preferences); code-split, always noindex.
const Newsletter = lazyRoute(() => import("./pages/Newsletter"));
const NewsletterConfirm = lazyRoute(() => import("./pages/newsletter/Confirm"));
const NewsletterOffersOff = lazyRoute(() => import("./pages/newsletter/OffersOff"));
const NewsletterUnsubscribe = lazyRoute(() => import("./pages/newsletter/Unsubscribe"));
const NewsletterPreferences = lazyRoute(() => import("./pages/newsletter/Preferences"));

// Which code-split chunk each path needs. index.js awaits this before the first render so a prerendered page is
// swapped for the live app without a "Loading..." flash.
const ROUTE_CHUNKS = [
  [/^\/market-pulse$/, MarketPulse],
  [/^\/data-centers$/, DataCenters],
  [/^\/bill-check$/, BillCheck],
  [/^\/help\/early-termination-fees$/, EarlyTerminationFees],
  [/^\/help$/, Help],
  [/^\/about$/, About],
  [/^\/help\/[^/]+$/, AnswerPage],
  [/^\/admin$/, AdminDashboard],
  [/^\/[^/]+\/utilities$/, Utilities],
  [/^\/[^/]+\/solar$/, Solar],
];

/** Resolves once the lazy chunk for `pathname` (if any) is loaded. Never rejects. */
export function preloadRouteChunk(pathname) {
  const p = String(pathname || "/").replace(/\/+$/, "") || "/";
  const hit = ROUTE_CHUNKS.find(([re]) => re.test(p));
  return hit ? hit[1].preload().catch(() => {}) : Promise.resolve();
}

const Loading = () => <div className="max-w-[1280px] mx-auto px-6 py-20 text-slate-500" role="status">Loading…</div>;

/** A render error in one page shows a message in that page instead of unmounting the app (QA-33). Resets on navigation. */
function RouteBoundary({ children }) {
  const { pathname } = useLocation();
  return <ErrorBoundary resetKey={pathname}>{children}</ErrorBoundary>;
}

function PublicShell() {
  return (
    <>
      {/* Site-wide structured data (Organization/WebSite/Service) -- one graph, every public
          route, so it's never re-declared per page. Real fields only, see lib/jsonLd.js. */}
      <JsonLd data={buildSiteJsonLd()} />
      <Header />
      {/* Self-gating: renders only where STATE_REGISTRY enables the tdu_banner feature (Texas). */}
      <TexasBanner />
      {/* Site-wide market ticker (real data only, QA-23) -- renders nothing until the first fetch resolves. */}
      <TickerTape />
      <main className="min-h-[60vh]">
        <RouteBoundary>
          <Suspense fallback={<Loading />}>
            <Outlet />
          </Suspense>
        </RouteBoundary>
      </main>
      <Footer />
    </>
  );
}

function AdminSeo() {
  return <Seo title="Admin" noindex />;
}

/**
 * Route table. Public state pages live under /:stateSlug/*, validated against
 * STATE_REGISTRY (src/lib/stateRegistry.js). Static national paths (market-pulse,
 * how-it-works, data-centers, admin) outrank the dynamic :stateSlug segment.
 * Unknown slugs (including New York) render NotFound.
 */
export function AppRoutes() {
  return (
    <RouteBoundary>
    <Routes>
      <Route element={<PublicShell />}>
        <Route index element={<Home />} />
        <Route path="market-pulse" element={<MarketPulse />} />
        <Route path="data-centers" element={<DataCenters />} />
        <Route path="how-it-works" element={<HowItWorks />} />
        {/* Legal pages (Privacy_Policy.md / Terms_Of_Use.md). Static paths outrank :stateSlug. */}
        <Route path="privacy" element={<Privacy />} />
        <Route path="terms" element={<Terms />} />
        {/* Bill-analysis (Technical Blueprint §1/§8). National, not state-scoped. */}
        <Route path="bill-check" element={<BillCheck />} />
        {/* ETF-exception education, national and standalone (ETF Placement Spec Section 3). */}
        <Route path="help/early-termination-fees" element={<EarlyTerminationFees />} />
        {/* Answer pages: hub plus one page per slug in src/lib/answerPages (unknown slugs render NotFound). */}
        <Route path="help" element={<Help />} />
        <Route path="about" element={<About />} />
        {/* Newsletter: the signup page renders NotFound unless REACT_APP_NEWSLETTER_SIGNUP_ENABLED=true; the token pages always work. */}
        <Route path="newsletter" element={<Newsletter />} />
        <Route path="newsletter/confirm" element={<NewsletterConfirm />} />
        <Route path="newsletter/offers-off" element={<NewsletterOffersOff />} />
        <Route path="newsletter/unsubscribe" element={<NewsletterUnsubscribe />} />
        <Route path="newsletter/preferences" element={<NewsletterPreferences />} />
        <Route path="help/:slug" element={<AnswerPage />} />

        {/* National account routes (Design Specification Section 12; "account" is a reserved slug in
            STATE_REGISTRY so it can never collide with a state). */}
        <Route path="account/login" element={<SignIn />} />
        <Route path="account/register" element={<Register />} />
        <Route path="account/verify" element={<VerifyEmail />} />
        <Route path="account" element={<AccountOverview />} />

        <Route path=":stateSlug" element={<StateGate />}>
          <Route index element={<Guard segment=""><StateLanding /></Guard>} />
          <Route path="plans" element={<Guard segment="plans"><Plans /></Guard>} />
          <Route path="plans/:id" element={<Guard segment="plans"><PlanDetail /></Guard>} />
          <Route path="providers" element={<Guard segment="providers"><Providers /></Guard>} />
          <Route path="enroll/:planId" element={<Guard segment="enroll"><Enroll /></Guard>} />
          <Route path="commercial" element={<Guard segment="commercial"><CommercialIntake /></Guard>} />
          <Route path="utilities" element={<Guard segment="utilities"><Utilities /></Guard>} />
          <Route path="solar" element={<Guard segment="solar"><Solar /></Guard>} />
          <Route path="esid-lookup" element={<Guard segment="esid-lookup"><EsidLookup /></Guard>} />
          <Route path="*" element={<NotFound />} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Route>

      <Route path="/admin/login" element={<><AdminSeo /><AdminLogin /></>} />
      <Route path="/admin" element={<><AdminSeo /><Suspense fallback={<Loading />}><AdminDashboard /></Suspense></>} />
    </Routes>
    </RouteBoundary>
  );
}

function App() {
  return (
    <div className="App bg-white text-black">
      <HelmetProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </HelmetProvider>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
